// Logo system for Dharmoday Seva Sansthan
// AI-generated premium logos (top tier) + coded SVG variants below.
// The selected key is stored in localStorage.dds_logo.

const P = "#C95B24"; // saffron
const S = "#1B4332"; // forest green
const A = "#F4C36B"; // sun accent (soft gold)
const D = "#8B1E3F"; // deep maroon accent
const G = "#0F5972"; // deep teal accent

// ═══════ AI-GENERATED IMAGE LOGOS ═══════
// Rendered as <img> tags — served from /generated_logos/*.png
const makeAiLogo = (fileName) => ({ size = 44 }) => (
  <img
    src={`/generated_logos/${fileName}`}
    alt="Dharmoday logo"
    width={size}
    height={size}
    style={{ objectFit: "contain", display: "inline-block" }}
    draggable={false}
  />
);

// User's official trust seal — the ORIGINAL uploaded design served as-is,
// framed in a clean white circle so it stands out on any background.
export const LogoOfficial = ({ size = 44 }) => {
  const dim = typeof size === "number" ? `${size}px` : size; // support "100%" too
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: dim,
        height: dim,
        borderRadius: "50%",
        background: "#FFFFFF",
        boxShadow: "0 2px 10px rgba(27, 67, 50, 0.18)",
        overflow: "hidden",
        flexShrink: 0,
      }}
    >
      <img
        src="/generated_logos/original_reference.jpeg"
        alt="Dharmoday Seva Sansthan"
        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        draggable={false}
      />
    </span>
  );
};

// Keep V2/V3 references for backwards compatibility but they now point to the same
// (correct) composed component.
export const LogoOfficialV2 = LogoOfficial;
export const LogoOfficialV3 = LogoOfficial;

export const LogoAiEmblem     = makeAiLogo("emblem_seal.png");
export const LogoAiNineRay    = makeAiLogo("nine_ray_sunrise.png");
export const LogoAiLotusHands = makeAiLogo("lotus_hands_diya.png");
export const LogoAiChakra     = makeAiLogo("chakra_om.png");
export const LogoAiPeacock    = makeAiLogo("peacock_om.png");
export const LogoAiMandala    = makeAiLogo("mandala_medallion.png");
export const LogoAiKalash     = makeAiLogo("kalash_dawn.png");
export const LogoAiSevaSun    = makeAiLogo("seva_sunrise.png");

// ═══════ PREMIUM UNIQUE DESIGNS ═══════

// P1. EMBLEM SEAL — circular govt-emblem style with sun burst rim
export const LogoEmblemSeal = ({ size = 44 }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} aria-label="Dharmoday logo">
    <defs>
      <radialGradient id="sealBg" cx="0.5" cy="0.35" r="0.8">
        <stop offset="0%" stopColor="#F0A272" />
        <stop offset="70%" stopColor={P} />
        <stop offset="100%" stopColor="#8E3B1D" />
      </radialGradient>
    </defs>
    <g transform="translate(32 32)">
      {Array.from({ length: 24 }).map((_, i) => {
        const long = i % 2 === 0;
        return (
          <line key={i} x1="0" y1={-27} x2="0" y2={long ? -30 : -28.5}
            stroke={A} strokeWidth={long ? 2.2 : 1.4} strokeLinecap="round"
            transform={`rotate(${(i * 360) / 24})`} />
        );
      })}
      <circle r="24" fill="url(#sealBg)" />
      <circle r="24" fill="none" stroke={S} strokeWidth="1.2" opacity="0.4" />
      <circle r="19" fill="none" stroke="#FDFBF7" strokeWidth="0.6" opacity="0.55" strokeDasharray="1 2" />
      {/* dots around inner ring */}
      {Array.from({ length: 9 }).map((_, i) => (
        <circle key={i} cx="0" cy="-15" r="1.2" fill="#FDFBF7" opacity="0.9"
          transform={`rotate(${(i * 360) / 9})`} />
      ))}
      <text x="0" y="7" fontFamily="'Rozha One', serif" fontSize="22" fill="#FDFBF7" textAnchor="middle" fontWeight="700">ॐ</text>
    </g>
  </svg>
);

// P2. NINE-RAY SUNRISE — 9 rays for 9 areas, Om rising
export const LogoNineRaySunrise = ({ size = 44 }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} aria-label="Dharmoday logo">
    <defs>
      <linearGradient id="nrs" x1="0" y1="1" x2="0" y2="0">
        <stop offset="0%" stopColor="#8E3B1D" />
        <stop offset="60%" stopColor={P} />
        <stop offset="100%" stopColor="#F0A272" />
      </linearGradient>
    </defs>
    {/* 9 rays fanning up */}
    {Array.from({ length: 9 }).map((_, i) => {
      const spread = 140; // total spread degrees
      const angle = -spread / 2 + (i * spread) / 8;
      const x2 = 32 + Math.sin((angle * Math.PI) / 180) * 24;
      const y2 = 50 - Math.cos((angle * Math.PI) / 180) * 24;
      return (
        <line key={i} x1="32" y1="50" x2={x2} y2={y2}
          stroke={i === 4 ? P : A} strokeWidth={i === 4 ? 2.4 : 1.6}
          strokeLinecap="round" opacity={i === 4 ? 1 : 0.75} />
      );
    })}
    {/* horizon */}
    <line x1="6" y1="50" x2="58" y2="50" stroke={S} strokeWidth="2.5" strokeLinecap="round" />
    {/* half sun */}
    <path d="M15 50 A17 17 0 0 1 49 50 Z" fill="url(#nrs)" />
    <text x="32" y="47" fontFamily="'Rozha One', serif" fontSize="17" fill="#FDFBF7" textAnchor="middle" fontWeight="700">ॐ</text>
  </svg>
);

// P3. LOTUS HANDS DIYA — two cupped-hands-as-lotus-petals holding a lit diya
export const LogoLotusHandsDiya = ({ size = 44 }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} aria-label="Dharmoday logo">
    <defs>
      <linearGradient id="lhdFlame" x1="0" y1="1" x2="0" y2="0">
        <stop offset="0%" stopColor={P} />
        <stop offset="60%" stopColor="#F5B15A" />
        <stop offset="100%" stopColor="#FFF3D6" />
      </linearGradient>
    </defs>
    {/* halo */}
    <circle cx="32" cy="18" r="10" fill={A} opacity="0.28" />
    {/* flame */}
    <path d="M32 6 C28 12 28 18 30 22 C31 23 32 23 32 24 C32 23 33 23 34 22 C36 18 36 12 32 6 Z" fill="url(#lhdFlame)" />
    <path d="M32 14 C31 16.5 31 19 31.5 20.5 C31.7 21 32 21 32 21.4 C32 21 32.3 21 32.5 20.5 C33 19 33 16.5 32 14 Z" fill="#FFF3D6" opacity="0.9" />
    {/* wick */}
    <rect x="31" y="24" width="2" height="3" fill={S} />
    {/* diya cup */}
    <path d="M22 27 Q32 32 42 27 L40 32 Q32 35 24 32 Z" fill={P} />
    {/* two hands cupping like petals */}
    <path d="M6 40 Q10 34 20 36 Q26 38 28 42 Q26 46 22 46 L10 46 Q6 46 6 42 Z" fill={S} />
    <path d="M58 40 Q54 34 44 36 Q38 38 36 42 Q38 46 42 46 L54 46 Q58 46 58 42 Z" fill={S} />
    {/* base */}
    <path d="M14 48 Q32 56 50 48 L46 54 Q32 58 18 54 Z" fill={S} />
    <line x1="14" y1="49" x2="50" y2="49" stroke="#FDFBF7" strokeWidth="0.6" opacity="0.35" />
  </svg>
);

// P4. CHAKRA OM FUSION — dharma chakra with Om at centre, spokes = 9
export const LogoChakraOmFusion = ({ size = 44 }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} aria-label="Dharmoday logo">
    <g transform="translate(32 32)">
      {/* outer rays */}
      {Array.from({ length: 18 }).map((_, i) => (
        <line key={i} x1="0" y1="-26" x2="0" y2="-30" stroke={A} strokeWidth="1.8"
          strokeLinecap="round" transform={`rotate(${(i * 360) / 18})`} opacity="0.85" />
      ))}
      {/* chakra ring */}
      <circle r="22" fill="none" stroke={P} strokeWidth="2.5" />
      <circle r="22" fill="#FDFBF7" />
      <circle r="22" fill="none" stroke={S} strokeWidth="0.6" opacity="0.35" />
      {/* 9 spokes */}
      {Array.from({ length: 9 }).map((_, i) => (
        <line key={i} x1="0" y1="-20" x2="0" y2="-10" stroke={P} strokeWidth="2"
          strokeLinecap="round" transform={`rotate(${(i * 360) / 9})`} />
      ))}
      {/* Om */}
      <circle r="8" fill={S} />
      <text x="0" y="4" fontFamily="'Rozha One', serif" fontSize="10" fill={A} textAnchor="middle" fontWeight="700">ॐ</text>
    </g>
  </svg>
);

// P5. PEACOCK FEATHER OM — very Indian & unique
export const LogoPeacockFeather = ({ size = 44 }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} aria-label="Dharmoday logo">
    <defs>
      <radialGradient id="peaEye" cx="0.5" cy="0.5" r="0.5">
        <stop offset="0%" stopColor={P} />
        <stop offset="60%" stopColor={S} />
        <stop offset="100%" stopColor="#0B2A20" />
      </radialGradient>
    </defs>
    {/* stem */}
    <path d="M32 58 Q30 44 32 22" stroke={S} strokeWidth="2" fill="none" strokeLinecap="round" />
    {/* filaments */}
    {[36, 40, 44, 48, 52].map((y, i) => (
      <g key={i}>
        <line x1="32" y1={y} x2={32 - 6 - i} y2={y - 2} stroke={S} strokeWidth="1" opacity="0.6" />
        <line x1="32" y1={y} x2={32 + 6 + i} y2={y - 2} stroke={S} strokeWidth="1" opacity="0.6" />
      </g>
    ))}
    {/* outer petal (feather blade) */}
    <path d="M32 6 C18 12 14 26 22 34 C26 38 30 38 32 34 C34 38 38 38 42 34 C50 26 46 12 32 6 Z" fill={G} />
    <path d="M32 10 C22 14 20 24 26 30 C29 32 31 32 32 30 C33 32 35 32 38 30 C44 24 42 14 32 10 Z" fill="#1A7A8A" />
    {/* eye */}
    <ellipse cx="32" cy="22" rx="8" ry="9" fill="url(#peaEye)" />
    <text x="32" y="26" fontFamily="'Rozha One', serif" fontSize="10" fill="#FDFBF7" textAnchor="middle" fontWeight="700">ॐ</text>
    <ellipse cx="30" cy="19" rx="1.5" ry="1" fill="#FDFBF7" opacity="0.6" />
  </svg>
);

// P6. MANDALA MEDALLION — concentric mandala rings with radiant Om
export const LogoMandalaMedallion = ({ size = 44 }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} aria-label="Dharmoday logo">
    <defs>
      <linearGradient id="mmCenter" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#F0A272" />
        <stop offset="100%" stopColor={P} />
      </linearGradient>
    </defs>
    <g transform="translate(32 32)">
      {/* outer scallops */}
      {Array.from({ length: 12 }).map((_, i) => (
        <path key={i} d="M0 -30 A3 3 0 0 1 0 -26 Z" fill={A}
          transform={`rotate(${(i * 360) / 12})`} />
      ))}
      <circle r="26" fill="none" stroke={S} strokeWidth="1.2" />
      {/* petal ring */}
      {Array.from({ length: 8 }).map((_, i) => (
        <ellipse key={i} cx="0" cy="-19" rx="3" ry="6" fill={S} opacity="0.8"
          transform={`rotate(${(i * 360) / 8})`} />
      ))}
      <circle r="14" fill="url(#mmCenter)" />
      <circle r="14" fill="none" stroke="#FDFBF7" strokeWidth="0.6" strokeDasharray="1 2" opacity="0.6" />
      <text x="0" y="6" fontFamily="'Rozha One', serif" fontSize="18" fill="#FDFBF7" textAnchor="middle" fontWeight="700">ॐ</text>
    </g>
  </svg>
);

// P7. WORDMARK — geometric "धर्मोदय" first-letter mark integrated with sun
export const LogoWordmark = ({ size = 44 }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} aria-label="Dharmoday logo">
    {/* sun disc backdrop */}
    <circle cx="32" cy="32" r="26" fill="none" stroke={P} strokeWidth="2.5" />
    {/* rays */}
    {[0, 45, 90, 135, 180, 225, 270, 315].map((r, i) => (
      <line key={i} x1="32" y1="32" x2="32" y2="6" stroke={A} strokeWidth="1.5" strokeLinecap="round"
        opacity="0.7" transform={`rotate(${r} 32 32)`} />
    ))}
    {/* filled arc bottom half */}
    <path d="M8 32 A24 24 0 0 0 56 32 Z" fill={P} opacity="0.15" />
    {/* the letter ध stylised */}
    <text x="32" y="42" fontFamily="'Rozha One', serif" fontSize="30" fill={S} textAnchor="middle" fontWeight="700">ध</text>
    {/* small Om accent above */}
    <text x="46" y="18" fontFamily="'Rozha One', serif" fontSize="10" fill={P} textAnchor="middle" fontWeight="700">ॐ</text>
  </svg>
);

// P8. SUNRISE SEVA — hands rising into sun (unique, dynamic)
export const LogoSevaSunrise = ({ size = 44 }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} aria-label="Dharmoday logo">
    <defs>
      <linearGradient id="ssSun" x1="0" y1="1" x2="0" y2="0">
        <stop offset="0%" stopColor="#8E3B1D" />
        <stop offset="60%" stopColor={P} />
        <stop offset="100%" stopColor={A} />
      </linearGradient>
    </defs>
    {/* rays */}
    {[-70, -50, -30, -10, 10, 30, 50, 70].map((r, i) => (
      <line key={i} x1="32" y1="30" x2={32 + Math.sin((r * Math.PI) / 180) * 26} y2={30 - Math.cos((r * Math.PI) / 180) * 26}
        stroke={A} strokeWidth="1.6" strokeLinecap="round" opacity="0.7" />
    ))}
    {/* sun disc */}
    <circle cx="32" cy="30" r="12" fill="url(#ssSun)" />
    {/* seva hands rising */}
    <path d="M14 58 L18 42 Q22 38 26 42 L26 58 Z" fill={S} />
    <path d="M50 58 L46 42 Q42 38 38 42 L38 58 Z" fill={S} />
    <path d="M26 46 Q32 40 38 46 L38 58 L26 58 Z" fill={S} opacity="0.85" />
    {/* soul spark on center hand */}
    <circle cx="32" cy="44" r="1.6" fill={A} />
  </svg>
);

// ═══════ EARLIER DESIGNS ═══════

// 1. NINE-PETAL LOTUS + DIYA — 9 petals = 9 areas of seva; diya = rise of dharma / light
export const LogoPetalDiya = ({ size = 44 }) => {
  const cx = 32, cy = 32;
  const petals = [];
  for (let i = 0; i < 9; i++) {
    const angle = (i * 360) / 9;
    petals.push(
      <ellipse key={i} cx={cx} cy={cy - 20} rx="5.4" ry="14" fill="url(#petalGrad)"
        transform={`rotate(${angle} ${cx} ${cy})`} opacity="0.95" />
    );
  }
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" aria-label="Dharmoday logo">
      <defs>
        <radialGradient id="petalGrad" cx="0.5" cy="0.2" r="0.9">
          <stop offset="0%" stopColor="#F0A272" />
          <stop offset="55%" stopColor={P} />
          <stop offset="100%" stopColor="#8E3B1D" />
        </radialGradient>
        <linearGradient id="flameGrad" x1="0" y1="1" x2="0" y2="0">
          <stop offset="0%" stopColor={P} />
          <stop offset="60%" stopColor="#F5B15A" />
          <stop offset="100%" stopColor="#FFF3D6" />
        </linearGradient>
      </defs>
      <circle cx={cx} cy={cy} r="30" fill="none" stroke={S} strokeOpacity="0.12" strokeWidth="1" />
      {petals}
      <circle cx={cx} cy={cy} r="9.5" fill={S} />
      <circle cx={cx} cy={cy} r="9.5" fill="none" stroke="#FDFBF7" strokeOpacity="0.35" strokeWidth="0.6" />
      <path d="M25 36 Q32 40 39 36 L37 38.4 Q32 41 27 38.4 Z" fill="#F5B15A" />
      <path d="M32 22 C29.5 25 29 28.6 30.4 30.8 C31.1 31.9 31.8 32.5 32 33 C32.2 32.5 32.9 31.9 33.6 30.8 C35 28.6 34.5 25 32 22 Z" fill="url(#flameGrad)" />
      <path d="M32 26 C31 27.6 31 29 31.6 30 C31.8 30.4 32 30.6 32 30.9 C32 30.6 32.2 30.4 32.4 30 C33 29 33 27.6 32 26 Z" fill="#FFF3D6" opacity="0.85" />
    </svg>
  );
};

// 2. SUNRISE + OM — literal "Dharmoday" (rise of dharma)
export const LogoSunriseOm = ({ size = 44 }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} aria-label="Dharmoday logo">
    <line x1="6" y1="52" x2="58" y2="52" stroke={S} strokeWidth="2.5" strokeLinecap="round" />
    {[-60, -40, -20, 0, 20, 40, 60].map((r, i) => (
      <line key={i} x1="32" y1="52" x2={32 + Math.sin((r * Math.PI) / 180) * 26} y2={52 - Math.cos((r * Math.PI) / 180) * 26}
        stroke={A} strokeWidth="1.6" strokeLinecap="round" opacity="0.75" />
    ))}
    <path d="M14 52 A18 18 0 0 1 50 52 Z" fill={P} />
    <text x="32" y="49" fontFamily="'Rozha One', serif" fontSize="20" fill="#FDFBF7" textAnchor="middle" fontWeight="700">ॐ</text>
  </svg>
);

// 3. LOTUS + OM — spiritual bloom
export const LogoLotusOm = ({ size = 44 }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} aria-label="Dharmoday logo">
    <g transform="translate(32 34)">
      {[-60, -30, 0, 30, 60].map((r, i) => (
        <ellipse key={i} cx="0" cy="-14" rx="7" ry="16" fill={S} opacity={i === 2 ? 1 : 0.85} transform={`rotate(${r})`} />
      ))}
      {[-80, 80].map((r, i) => (
        <ellipse key={i} cx="0" cy="-10" rx="6" ry="14" fill={S} opacity="0.55" transform={`rotate(${r})`} />
      ))}
      <circle cx="0" cy="-4" r="11" fill={P} />
      <text x="0" y="2" fontFamily="'Rozha One', serif" fontSize="15" fill="#FDFBF7" textAnchor="middle" fontWeight="700">ॐ</text>
    </g>
  </svg>
);

// 4. DHARMA CHAKRA + SUN
export const LogoChakraSun = ({ size = 44 }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} aria-label="Dharmoday logo">
    <line x1="8" y1="48" x2="56" y2="48" stroke={S} strokeWidth="2" strokeLinecap="round" />
    {[-40, -20, 0, 20, 40].map((r, i) => (
      <line key={i} x1="32" y1="48" x2={32 + Math.sin((r * Math.PI) / 180) * 22} y2={48 - Math.cos((r * Math.PI) / 180) * 22}
        stroke={A} strokeWidth="1.4" strokeLinecap="round" opacity="0.7" />
    ))}
    <g transform="translate(32 30)">
      <circle r="13" fill="none" stroke={P} strokeWidth="2.5" />
      <circle r="2.5" fill={P} />
      {[0, 30, 60, 90, 120, 150].map((r, i) => (
        <line key={i} x1="0" y1="-11.5" x2="0" y2="11.5" stroke={P} strokeWidth="1.8" transform={`rotate(${r})`} strokeLinecap="round" />
      ))}
    </g>
  </svg>
);

// 5. HANDS HOLDING SUN — seva + udaya
export const LogoHandsSun = ({ size = 44 }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} aria-label="Dharmoday logo">
    <circle cx="32" cy="24" r="11" fill={P} />
    {[-90, -45, 0, 45, 90].map((r, i) => (
      <line key={i} x1="32" y1="24" x2={32 + Math.sin((r * Math.PI) / 180) * 18} y2={24 - Math.cos((r * Math.PI) / 180) * 18}
        stroke={P} strokeWidth="1.6" strokeLinecap="round" opacity="0.6" />
    ))}
    <path d="M10 44 Q16 34 24 40 L32 44 L40 40 Q48 34 54 44 Q54 54 44 56 L20 56 Q10 54 10 44 Z" fill={S} />
    <path d="M20 47 Q32 41 44 47" stroke="#FDFBF7" strokeWidth="1.4" fill="none" opacity="0.4" />
  </svg>
);

// 6. TREE OF DHARMA
export const LogoTreeOm = ({ size = 44 }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} aria-label="Dharmoday logo">
    <circle cx="32" cy="22" r="16" fill={S} />
    <circle cx="22" cy="24" r="8" fill={S} />
    <circle cx="42" cy="24" r="8" fill={S} />
    <circle cx="32" cy="14" r="7" fill={S} />
    <rect x="29" y="34" width="6" height="16" fill={P} rx="1" />
    <text x="32" y="27" fontFamily="'Rozha One', serif" fontSize="15" fill={A} textAnchor="middle" fontWeight="700">ॐ</text>
    <line x1="14" y1="52" x2="50" y2="52" stroke={S} strokeWidth="2" strokeLinecap="round" />
  </svg>
);

// 7. ध MONOGRAM in a sun arch
export const LogoMonogram = ({ size = 44 }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} aria-label="Dharmoday logo">
    <path d="M8 44 A24 24 0 0 1 56 44" fill="none" stroke={P} strokeWidth="3" strokeLinecap="round" />
    {[-70, -50, -30, -10, 10, 30, 50, 70].map((r, i) => (
      <line key={i}
        x1={32 + Math.sin((r * Math.PI) / 180) * 25}
        y1={44 - Math.cos((r * Math.PI) / 180) * 25}
        x2={32 + Math.sin((r * Math.PI) / 180) * 29}
        y2={44 - Math.cos((r * Math.PI) / 180) * 29}
        stroke={A} strokeWidth="1.8" strokeLinecap="round" />
    ))}
    <circle cx="32" cy="14" r="2.4" fill={P} />
    <text x="32" y="52" fontFamily="'Rozha One', serif" fontSize="22" fill={S} textAnchor="middle" fontWeight="700">ध</text>
  </svg>
);

// 8. MANDALA + SUN — 8-point mandala frame with sun disk
export const LogoMandalaSun = ({ size = 44 }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} aria-label="Dharmoday logo">
    <g transform="translate(32 32)">
      {[0, 45, 90, 135].map((r, i) => (
        <line key={i} x1="-26" y1="0" x2="26" y2="0" stroke={S} strokeOpacity="0.35" strokeWidth="1" transform={`rotate(${r})`} />
      ))}
      {[0, 22.5, 45, 67.5, 90, 112.5, 135, 157.5].map((r, i) => (
        <circle key={i} cx="24" cy="0" r="2.2" fill={A} transform={`rotate(${r})`} />
      ))}
      <circle r="18" fill="none" stroke={P} strokeWidth="1.5" strokeDasharray="2 3" opacity="0.7" />
      <circle r="12" fill={P} />
      <text x="0" y="4" fontFamily="'Rozha One', serif" fontSize="15" fill="#FDFBF7" textAnchor="middle" fontWeight="700">ॐ</text>
    </g>
  </svg>
);

// 9. PURNA KALASH — sacred pot with mango leaves & coconut (auspicious in dharma)
export const LogoKalash = ({ size = 44 }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} aria-label="Dharmoday logo">
    {/* coconut */}
    <ellipse cx="32" cy="14" rx="6" ry="7" fill="#8B4513" />
    {/* mango leaves */}
    <path d="M20 22 Q26 12 30 18 Q26 22 20 22 Z" fill={S} />
    <path d="M44 22 Q38 12 34 18 Q38 22 44 22 Z" fill={S} />
    <path d="M26 22 Q30 14 32 20 Q30 24 26 22 Z" fill={S} opacity="0.85" />
    <path d="M38 22 Q34 14 32 20 Q34 24 38 22 Z" fill={S} opacity="0.85" />
    {/* kalash rim */}
    <rect x="20" y="22" width="24" height="4" rx="1" fill={P} />
    {/* pot body */}
    <path d="M22 26 Q14 34 18 46 Q22 56 32 56 Q42 56 46 46 Q50 34 42 26 Z" fill={P} />
    {/* pot decorative band */}
    <path d="M18 36 Q32 40 46 36" stroke={A} strokeWidth="1.5" fill="none" />
    <path d="M20 42 Q32 46 44 42" stroke={A} strokeWidth="1.2" fill="none" opacity="0.7" />
  </svg>
);

// 10. Om MEDALLION — Om inside a sun-ray medallion
export const LogoOmMedallion = ({ size = 44 }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} aria-label="Dharmoday logo">
    <g transform="translate(32 32)">
      {Array.from({ length: 16 }).map((_, i) => (
        <line key={i} x1="0" y1="-24" x2="0" y2="-30" stroke={A} strokeWidth="2" strokeLinecap="round"
          transform={`rotate(${(i * 360) / 16})`} />
      ))}
      <circle r="22" fill={P} />
      <circle r="22" fill="none" stroke={S} strokeWidth="1.5" strokeOpacity="0.35" />
      <text x="0" y="7" fontFamily="'Rozha One', serif" fontSize="26" fill="#FDFBF7" textAnchor="middle" fontWeight="700">ॐ</text>
    </g>
  </svg>
);

// 11. SUN over MOUNTAINS — landscape rise
export const LogoMountainSun = ({ size = 44 }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} aria-label="Dharmoday logo">
    {/* rays */}
    {[-60, -30, 0, 30, 60].map((r, i) => (
      <line key={i} x1="32" y1="28" x2={32 + Math.sin((r * Math.PI) / 180) * 22} y2={28 - Math.cos((r * Math.PI) / 180) * 22}
        stroke={A} strokeWidth="1.5" strokeLinecap="round" opacity="0.7" />
    ))}
    {/* sun */}
    <circle cx="32" cy="28" r="9" fill={P} />
    {/* mountains */}
    <path d="M4 52 L20 30 L28 40 L36 26 L48 42 L60 52 Z" fill={S} />
    <path d="M4 52 L20 30 L28 40 L36 26 L48 42 L60 52 Z" fill="none" stroke={S} strokeWidth="1" />
    {/* snow caps */}
    <path d="M18 33 L20 30 L22 33 Z" fill="#FDFBF7" />
    <path d="M34 29 L36 26 L38 29 Z" fill="#FDFBF7" />
    {/* ground */}
    <line x1="4" y1="54" x2="60" y2="54" stroke={S} strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

// 12. GRAND DIYA — large diya (oil lamp) with flame, offering base
export const LogoGrandDiya = ({ size = 44 }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} aria-label="Dharmoday logo">
    <defs>
      <linearGradient id="gdFlame" x1="0" y1="1" x2="0" y2="0">
        <stop offset="0%" stopColor={P} />
        <stop offset="65%" stopColor="#F5B15A" />
        <stop offset="100%" stopColor="#FFF3D6" />
      </linearGradient>
    </defs>
    {/* halo */}
    <circle cx="32" cy="22" r="14" fill={A} opacity="0.25" />
    {/* flame */}
    <path d="M32 8 C27 16 26 24 30 30 C31 32 32 32 32 34 C32 32 33 32 34 30 C38 24 37 16 32 8 Z" fill="url(#gdFlame)" />
    <path d="M32 16 C30 20 30 24 31 26 C31.5 27 32 27 32 28 C32 27 32.5 27 33 26 C34 24 34 20 32 16 Z" fill="#FFF3D6" opacity="0.9" />
    {/* wick */}
    <rect x="31" y="34" width="2" height="4" fill={S} />
    {/* diya bowl */}
    <path d="M14 38 Q32 46 50 38 L46 46 Q32 52 18 46 Z" fill={P} />
    <path d="M14 38 Q32 46 50 38" stroke={S} strokeWidth="1" fill="none" opacity="0.4" />
    {/* base */}
    <rect x="22" y="50" width="20" height="4" rx="1" fill={S} />
  </svg>
);

// 13. BOOK + SUN — light of knowledge (education pillar)
export const LogoBookSun = ({ size = 44 }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} aria-label="Dharmoday logo">
    {/* rays */}
    {[-50, -25, 0, 25, 50].map((r, i) => (
      <line key={i} x1="32" y1="26" x2={32 + Math.sin((r * Math.PI) / 180) * 22} y2={26 - Math.cos((r * Math.PI) / 180) * 22}
        stroke={A} strokeWidth="1.6" strokeLinecap="round" opacity="0.75" />
    ))}
    {/* sun */}
    <circle cx="32" cy="26" r="8" fill={P} />
    {/* open book */}
    <path d="M8 40 L30 44 L30 56 L8 52 Z" fill={S} />
    <path d="M56 40 L34 44 L34 56 L56 52 Z" fill={S} />
    <path d="M30 44 L34 44 L34 56 L30 56 Z" fill={S} opacity="0.6" />
    {/* page lines */}
    <line x1="12" y1="46" x2="26" y2="48.5" stroke="#FDFBF7" strokeOpacity="0.4" strokeWidth="0.8" />
    <line x1="12" y1="49" x2="26" y2="51.5" stroke="#FDFBF7" strokeOpacity="0.4" strokeWidth="0.8" />
    <line x1="38" y1="48.5" x2="52" y2="46" stroke="#FDFBF7" strokeOpacity="0.4" strokeWidth="0.8" />
    <line x1="38" y1="51.5" x2="52" y2="49" stroke="#FDFBF7" strokeOpacity="0.4" strokeWidth="0.8" />
  </svg>
);

export const LOGO_VARIANTS = {
  // Official trust emblem — SVG-composed with correct Devanagari text
  official:    { Component: LogoOfficial,     label: "⭐ Official Trust Emblem",   meaning: "Circular seal with sun, family, plant, hand — correct Devanagari text overlaid via SVG." },
  // AI-generated premium logos
  ai_lotus:    { Component: LogoAiLotusHands, label: "🎨 Lotus Hands + Diya",  meaning: "Two forest-green hands as lotus petals cradling a glowing diya." },
  ai_seva:     { Component: LogoAiSevaSun,    label: "🎨 Seva Sunrise",        meaning: "Rising sun cradled in two open hands — the act of seva." },
  ai_peacock:  { Component: LogoAiPeacock,    label: "🎨 Peacock Feather",     meaning: "Sacred peacock feather — uniquely Indian and elegant." },
  ai_emblem:   { Component: LogoAiEmblem,     label: "🎨 Emblem Seal",         meaning: "Official-seal look with radiant sun rim, Om at heart." },
  ai_chakra:   { Component: LogoAiChakra,     label: "🎨 Dharma Chakra",       meaning: "Nine-spoke chakra with sun rays — balanced & sacred." },
  ai_mandala:  { Component: LogoAiMandala,    label: "🎨 Mandala Medallion",   meaning: "Concentric mandala rings radiating divine geometry." },
  ai_kalash:   { Component: LogoAiKalash,     label: "🎨 Purna Kalash",        meaning: "The sacred pot at dawn — abundance & auspiciousness." },
  ai_ninerays: { Component: LogoAiNineRay,    label: "🎨 Nine-Ray Sunrise",    meaning: "Nine golden rays over half-sun — nine areas of seva." },
  // Premium coded designs
  emblem:  { Component: LogoEmblemSeal,       label: "✨ Emblem Seal (SVG)",   meaning: "Vector-style seal with 9 stars & radiant rim." },
  ninerays:{ Component: LogoNineRaySunrise,   label: "✨ Nine-Ray Sunrise (SVG)", meaning: "Nine golden rays for nine areas — flat vector." },
  lhdiya:  { Component: LogoLotusHandsDiya,   label: "✨ Lotus Hands + Diya (SVG)", meaning: "Vector lotus-hands cradling eternal flame." },
  cofusion:{ Component: LogoChakraOmFusion,   label: "✨ Chakra + Om (SVG)",   meaning: "9-spoke dharma wheel with Om." },
  peacock: { Component: LogoPeacockFeather,   label: "✨ Peacock Feather (SVG)", meaning: "Vector peacock feather with Om at eye." },
  medallion2:{ Component: LogoMandalaMedallion, label: "✨ Mandala Medallion (SVG)", meaning: "Vector mandala rings." },
  wordmark:{ Component: LogoWordmark,         label: "✨ ध Wordmark",          meaning: "Modern geometric mark with ध + rising Om." },
  sevasun: { Component: LogoSevaSunrise,      label: "✨ Seva Sunrise (SVG)",  meaning: "Vector hands rising into a saffron sun." },
  // Classic set
  petals:  { Component: LogoPetalDiya,  label: "9-Petal Lotus + Diya", meaning: "9 petals = 9 areas of seva. Diya = rise of light." },
  sunrise: { Component: LogoSunriseOm,  label: "Sunrise + Om",         meaning: "Literal 'Dharmoday' — rise of dharma." },
  lotus:   { Component: LogoLotusOm,    label: "Lotus + Om",           meaning: "Spiritual bloom around sacred sound." },
  chakra:  { Component: LogoChakraSun,  label: "Dharma Chakra + Sun",  meaning: "Wheel of dharma above rising sun." },
  hands:   { Component: LogoHandsSun,   label: "Hands Holding Sun",    meaning: "Seva cupping the light of a new day." },
  tree:    { Component: LogoTreeOm,     label: "Tree of Dharma",       meaning: "Rooted, sheltering, evergreen seva." },
  monogram:{ Component: LogoMonogram,   label: "ध Monogram",           meaning: "Elegant Devanagari mark under an arch." },
  mandala: { Component: LogoMandalaSun, label: "Mandala + Om",         meaning: "Sacred geometry radiating the sound of Om." },
  kalash:  { Component: LogoKalash,     label: "Purna Kalash",         meaning: "The sacred pot — symbol of abundance & dharma." },
  medallion:{ Component: LogoOmMedallion, label: "Om Medallion",       meaning: "Bold, coin-like sun with Om at the centre." },
  mountain:{ Component: LogoMountainSun,label: "Sun over Mountains",   meaning: "Rise over the earth — nature-rooted dharma." },
  diya:    { Component: LogoGrandDiya,  label: "Grand Diya",           meaning: "The single flame that lights a thousand lives." },
  book:    { Component: LogoBookSun,    label: "Light of Knowledge",   meaning: "Sun rising over an open book — shiksha." },
};

export const DEFAULT_LOGO = "official";

const Logo = ({ size = 44, variant, className = "" }) => {
  // Priority: explicit variant > user's picked variant in localStorage > default.
  const stored = typeof window !== "undefined" ? window.localStorage.getItem("dds_logo") : null;
  const key = variant || stored || DEFAULT_LOGO;
  const V = LOGO_VARIANTS[key] || LOGO_VARIANTS[DEFAULT_LOGO];
  const Cmp = V.Component;
  // If a className is provided we override the fixed pixel size so responsive Tailwind
  // classes (w-xx h-xx) can drive the actual dimensions.
  return (
    <span className={className} style={className ? { display: "inline-flex" } : undefined}>
      <Cmp size={className ? "100%" : size} />
    </span>
  );
};

export default Logo;
